import { Box } from '@mui/material';
import { type ApexOptions } from 'apexcharts';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { useAppSelector } from 'store/hook';
import { convertStdDateString } from 'utils/helpers/dateTime';
import { isDarkTheme } from 'utils/theme';

interface IndividualData {
  asset_id: string;
  timestamp: number;
  work_order_number: string;
  total_cost_asset: number;
  total_labour_cost: number;
  total_part_cost: number;
}

interface SelectedAssetCostData {
  data: IndividualData[];
  name: string; // the name of the asset
}

interface AreaChartProps {
  data: SelectedAssetCostData[] | any[];
  selectedAssetsCostType: string[];
}

export const AreaChart = ({ data, selectedAssetsCostType }: AreaChartProps) => {
  const theme = useAppSelector((state) => state.authReducer.customer.theme);
  const enableDarkTheme = isDarkTheme(theme);

  // Memoize series data to avoid recomputation
  const series = useMemo(() => {
    return data.flatMap((asset) =>
      selectedAssetsCostType.map((costType) => {
        const isLabour = costType === 'total_labour_cost';
        const color = isLabour ? '#4caf50' : '#ff9800';

        return {
          name: `${asset.name} - ${costType.replace(/_/g, ' ')}`,
          data: asset.data.map((d: any) => ({
            x: dayjs.unix(d.timestamp).toDate(),
            y: d[costType as keyof typeof d] ?? 0, // Fallback to 0 if value missing
          })),
          color,
        };
      })
    );
  }, [data, selectedAssetsCostType]);

  const options: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'area',
        toolbar: { show: false },
        zoom: { enabled: false, allowMouseWheelZoom: false },
      },
      dataLabels: { enabled: false },
      stroke: { curve: 'smooth' },
      xaxis: {
        type: 'datetime',
        labels: {
          style: { colors: enableDarkTheme ? '#fff' : '#000' },
          datetimeUTC: false,
          formatter: (value, timestamp) => {
            if (!timestamp) return '';
            return convertStdDateString(timestamp);
          },
        },
      },
      yaxis: {
        labels: {
          style: { colors: enableDarkTheme ? '#fff' : '#000' },
          formatter: (value) => value?.toFixed(2) ?? '0.00',
        },
      },
      tooltip: {
        theme: enableDarkTheme ? 'dark' : 'light',
      },
      grid: {
        borderColor: enableDarkTheme ? '#555' : '#eee',
        strokeDashArray: 5,
      },
      legend: {
        labels: { colors: enableDarkTheme ? '#fff' : '#000' },
        position: 'top',
        horizontalAlign: 'left',
        showForSingleSeries: true,
      },
      colors: series.map((s) => s.color), // Apply custom colors to series
    }),
    [enableDarkTheme, series]
  );

  return (
    <Box sx={{ height: '330px', width: '100%', margin: 0 }}>
      <Chart
        height="100%"
        width="100%"
        options={options}
        series={series}
        type="area"
      />
    </Box>
  );
};

export default AreaChart;
